import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Layout } from './components/ui/Layout';
import { useAccount } from './hooks/useAccount';
import { ChangelogPage } from './pages/ChangelogPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { ContactPage } from './pages/ContactPage';
import { DashboardPage } from './pages/DashboardPage';
import { DocsOverviewPage } from './pages/docs/DocsOverviewPage';
import { DocsSearchPage } from './pages/docs/DocsSearchPage';
import { LandingPage } from './pages/LandingPage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { RefundsPage } from './pages/legal/RefundsPage';
import { TermsPage } from './pages/legal/TermsPage';
import { PricingPage } from './pages/PricingPage';
import { SignInPage } from './pages/SignInPage';
import { VerifyPage } from './pages/VerifyPage';

/**
 * Routes live outside `/api`, which the Worker keeps for itself. `/auth/verify`
 * arrives in an email that may already be sitting in an inbox, so it cannot
 * move without breaking links already out in the world, see AGENTS.md.
 *
 * Access:
 * - guest only: `/`, `/signin` (signed-in → `/app`, no second magic-link flow)
 * - authed only: `/app` (guest → `/signin`)
 * - public: everything else; `/auth/verify` short-circuits when already signed in
 *
 * `/` is the acquisition surface. The product is still the API and (soon) MCP;
 * this app also issues keys and hosts public docs for `/v1`.
 */
export function App() {
    return (
        <Layout>
            <ScrollToTop />
            <Routes>
                <Route
                    path="/"
                    element={
                        <RequireGuest>
                            <LandingPage />
                        </RequireGuest>
                    }
                />
                <Route
                    path="/signin"
                    element={
                        <RequireGuest>
                            <SignInPage />
                        </RequireGuest>
                    }
                />
                <Route path="/auth/verify" element={<VerifyPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/changelog" element={<ChangelogPage />} />
                <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

                {/* Public integrator docs, only the customer surface under /v1. */}
                <Route path="/docs" element={<DocsOverviewPage />} />
                <Route path="/docs/search" element={<DocsSearchPage />} />

                {/* What a payment processor's review expects to find. */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/refunds" element={<RefundsPage />} />
                <Route path="/contact" element={<ContactPage />} />

                <Route
                    path="/app"
                    element={
                        <RequireAuth>
                            <DashboardPage />
                        </RequireAuth>
                    }
                />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Layout>
    );
}

function AuthLoading() {
    return <p className="py-16 text-center text-sm text-text-muted">Loading…</p>;
}

/** Console and anything else that needs a live session. */
function RequireAuth({ children }: { children: React.ReactNode }) {
    const { data, isPending } = useAccount();

    if (isPending) return <AuthLoading />;
    if (!data) return <Navigate to="/signin" replace />;

    return <>{children}</>;
}

/**
 * Marketing and the magic-link form. A signed-in user has no business starting
 * another login; switch accounts via Sign out first.
 */
function RequireGuest({ children }: { children: React.ReactNode }) {
    const { data, isPending } = useAccount();

    if (isPending) return <AuthLoading />;
    if (data) return <Navigate to="/app" replace />;

    return <>{children}</>;
}

/** SPA keeps document scroll across route swaps; reset to the top on navigate. */
function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}
